import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useState } from "react";
import { Form, Input, Button, Alert, Typography, Card, Flex, theme } from "antd";
import { supabase } from "@/configs/supabase/config";

const LoginSearchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
  message: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/login")({
  validateSearch: LoginSearchSchema.parse,
  beforeLoad: async ({ search }) => {
    const sb_Auth_GetSession = await supabase.auth.getSession();
    if (sb_Auth_GetSession.data.session) {
      throw redirect({ to: search.redirect || "/" });
    }
  },
  component: Page_Login,
});

function Page_Login() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { redirect: redirectTo, message } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { email: string; password: string }) => {
    setError(null);
    setLoading(true);

    const sb_Auth_SignIn = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });

    setLoading(false);

    if (sb_Auth_SignIn.error) {
      setError(sb_Auth_SignIn.error.message);
      return;
    }

    navigate({ to: redirectTo || "/" });
  };

  return (
    <Card style={{ width: 400 }} styles={{ body: { padding: token.paddingXL } }}>
      <Flex vertical align="center" gap={token.marginLG}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Insurance Hub
        </Typography.Title>
      </Flex>

      {message === "password-updated" && (
        <Alert
          message="Password updated successfully. Please sign in."
          type="success"
          showIcon
          style={{ marginTop: token.marginMD, marginBottom: token.marginSM }}
        />
      )}

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginTop: token.marginMD, marginBottom: token.marginSM }}
        />
      )}

      <Form layout="vertical" onFinish={handleSubmit} autoComplete="on" style={{ marginTop: token.marginLG }}>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input size="large" placeholder="you@company.com" autoFocus />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: "Password is required" }]}
        >
          <Input.Password size="large" placeholder="Password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: token.marginXS }}>
          <Button type="primary" htmlType="submit" size="large" block loading={loading}>
            Sign in
          </Button>
        </Form.Item>
      </Form>

      <Flex justify="center" style={{ marginTop: token.marginSM }}>
        <Typography.Link
          onClick={() => navigate({ to: "/reset-password" as string })}
          style={{ fontSize: token.fontSizeSM }}
        >
          Forgot password?
        </Typography.Link>
      </Flex>
    </Card>
  );
}
